// client/src/components/leads/b2c/VisitHistory.js
// UPDATED: Added delete button for each visit entry
// Uses customerAPI service for consistency

import React, { useState, useEffect } from 'react';
import { customerAPI } from '../../../services/customerAPI';

export default function VisitHistory({ customerId, onVisitDeleted }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadVisits();
  }, [customerId]);

  const loadVisits = async () => {
    try {
      setLoading(true);
      const response = await customerAPI.getVisitHistory(customerId);
      
      if (response.success) {
        setVisits(response.data || []);
      }
    } catch (error) {
      console.error('Error loading visits:', error);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Delete visit function
  const handleDeleteVisit = async (visitId) => {
    if (!window.confirm('Are you sure you want to delete this visit? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingId(visitId);
      
      const response = await customerAPI.deleteVisit(customerId, visitId);
      
      if (response.success) {
        // Reload visits to show updated list
        await loadVisits();
        
        // Notify parent to refresh customer data (for health metrics)
        if (onVisitDeleted) {
          await onVisitDeleted();
        }
      } else {
        alert(response.error || 'Failed to delete visit');
      }
    } catch (error) {
      console.error('Error deleting visit:', error);
      alert('Failed to delete visit');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `£${parseFloat(amount).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (visits.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 text-4xl mb-2">📋</div>
        <p className="text-gray-500 text-sm">No visits recorded yet</p>
        <p className="text-gray-400 text-xs mt-1">Click "Add Visit" to record the first visit</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      
      {/* Visit List */}
      {visits.map((visit) => (
        <div
          key={visit.id}
          className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-start justify-between gap-4">
            
            {/* Left: Service & Date */}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-blue-600 text-sm">💼</span>
                <h4 className="font-medium text-gray-900">{visit.service}</h4>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {formatDate(visit.visitDate)}
              </p>
              {visit.notes && (
                <p className="text-sm text-gray-600 mt-2 italic">
                  {visit.notes}
                </p>
              )}
            </div>

            {/* Right: Amount & Delete Button */}
            <div className="text-right flex flex-col items-end gap-2">
              <div className="text-lg font-semibold text-green-600">
                {formatCurrency(visit.amount)}
              </div>
              {visit.duration && (
                <div className="text-xs text-gray-500">
                  {visit.duration} mins
                </div>
              )}
              
              {/* NEW: Delete Button */}
              <button
                onClick={() => handleDeleteVisit(visit.id)}
                disabled={deletingId === visit.id}
                className="text-xs text-red-600 hover:text-red-700 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingId === visit.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>

          </div>
        </div>
      ))}

      {/* Summary Footer */}
      <div className="pt-4 border-t border-gray-200">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Total Visits:</span>
          <span className="font-semibold text-gray-900">{visits.length}</span>
        </div>
        <div className="flex justify-between text-sm mt-2">
          <span className="text-gray-600">Total Spent:</span>
          <span className="font-semibold text-green-600">
            {formatCurrency(
              visits.reduce((sum, visit) => sum + parseFloat(visit.amount), 0)
            )}
          </span>
        </div>
      </div>

    </div>
  );
}